"use client";

import { motion } from "framer-motion";
import { Crown, Medal, Star, TrendingUp, Trophy } from "lucide-react";

const sampleEntries = [
  { rank: 1, name: "Sample Student A", score: "98.4%", points: "9,840 pts", medal: "gold" },
  { rank: 2, name: "Sample Student B", score: "96.7%", points: "9,670 pts", medal: "silver" },
  { rank: 3, name: "Sample Student C", score: "95.2%", points: "9,520 pts", medal: "bronze" },
] as const;

export default function LeaderboardPreview() {
  return (
    <div className="space-y-4 bg-[#07101F] p-4 text-white sm:p-5">
      <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-brand-gold">
              Illustrative preview
            </p>
            <h2 className="mt-1 text-2xl font-black tracking-tight sm:text-3xl">
              Competition leaderboard
            </h2>
            <p className="mt-2 text-xs leading-5 text-white/50">
              Example rankings showing how competitive results can be presented.
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-brand-gold/30 bg-brand-gold/10 text-brand-gold">
            <Trophy className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {sampleEntries.map((student, index) => (
          <motion.div
            key={student.rank}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.07 }}
            whileHover={{ y: -3 }}
            className="rounded-2xl border border-white/10 bg-white/[0.035] p-4"
          >
            <div className="flex items-center justify-between">
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl border ${
                student.medal === "gold"
                  ? "border-brand-gold/35 bg-brand-gold/10 text-brand-gold"
                  : student.medal === "silver"
                    ? "border-white/15 bg-white/[0.06] text-white/70"
                    : "border-orange-400/25 bg-orange-400/10 text-orange-300"
              }`}>
                {student.rank === 1 ? <Crown className="h-5 w-5" /> : <Medal className="h-5 w-5" />}
              </div>
              <span className="text-2xl font-black">#{student.rank}</span>
            </div>
            <h3 className="mt-5 text-sm font-bold">{student.name}</h3>
            <p className="mt-1 text-xs text-white/45">{student.score} sample score</p>
            <span className="mt-4 inline-flex rounded-full border border-brand-gold/20 bg-brand-gold/[0.07] px-3 py-1.5 text-xs font-bold text-brand-gold">
              {student.points}
            </span>
          </motion.div>
        ))}
      </div>

      <div className="rounded-2xl border border-brand-gold/25 bg-gradient-to-r from-brand-gold/[0.08] via-white/[0.035] to-brand-gold/[0.04] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/30 bg-brand-gold/10 text-brand-gold">
            <Star className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold">Your performance, clearly presented</h3>
            <p className="mt-1 text-xs leading-5 text-white/50">
              Actual ranks and scores depend on the published competition and its results.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SummaryCard icon={<TrendingUp className="h-4 w-4" />} title="Performance" value="Score trends" />
        <SummaryCard icon={<Trophy className="h-4 w-4" />} title="Competition" value="Rank by results" />
      </div>
    </div>
  );
}

function SummaryCard({ icon, title, value }: { icon: React.ReactNode; title: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{title}</p>
          <p className="mt-1 text-sm font-bold">{value}</p>
        </div>
        <div className="rounded-lg border border-brand-gold/20 bg-brand-gold/10 p-2 text-brand-gold">{icon}</div>
      </div>
    </div>
  );
}
